import React from "react";
import { ASSETS, SCHOOL_INFO } from "@/lib/assets";
import { CheckCircle2, Target, Eye, Compass, ScrollText } from "lucide-react";

export default function About() {
  return (
    <div className="bg-white" data-testid="page-about">
      {/* Hero */}
      <section className="relative bg-[#1E3A8A] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={ASSETS.building} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/95 via-[#1E3A8A]/85 to-[#1E3A8A]/70" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4"><span>À propos</span></div>
          <h1 className="mazid-headline text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-4xl">
            Une institution au service de <span className="text-[#FBBF24]">l&apos;avenir</span> de la Guinée.
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl leading-relaxed">
            Les Écoles Mazid, c&apos;est plus qu&apos;une école : c&apos;est une famille, une vision, une promesse de réussite.
          </p>
          <a href="/agrements" className="inline-flex items-start gap-2 mt-6 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-slate-100 hover:bg-white/20 transition-colors" data-testid="about-arrete">
            <ScrollText className="h-5 w-5 text-[#FBBF24] flex-shrink-0 mt-0.5" />
            <span>Établissement agréé — <strong className="text-[#FBBF24]">{SCHOOL_INFO.arrete}</strong> · Voir nos agréments →</span>
          </a>
        </div>
      </section>

      {/* Founder section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={ASSETS.founder} alt="Fondateur" className="w-full rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -right-6 bg-[#FBBF24] text-[#1E3A8A] p-5 rounded-xl shadow-lg max-w-[200px]">
                <div className="font-heading font-extrabold text-2xl leading-none">&quot;Faisons mieux&quot;</div>
                <div className="text-xs mt-2 font-semibold opacity-80">— La devise du fondateur</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="chip mb-4"><span>Mot du fondateur</span></div>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1E3A8A] leading-tight tracking-tight mb-6">
              Construire l&apos;école que nos enfants méritent.
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Lorsque j&apos;ai fondé <strong className="text-[#1E3A8A]">Les Écoles Mazid</strong>, j&apos;avais une conviction profonde : la Guinée mérite une école qui forme des têtes bien faites, des cœurs généreux et des mains habiles. Une école qui ne se contente pas de transmettre des connaissances, mais qui éveille des consciences.
              </p>
              <p>
                Notre mission est simple et exigeante : <strong>former, accompagner et révéler le potentiel de chaque enfant</strong>. Chaque élève qui franchit nos portes est unique, et c&apos;est notre devoir de découvrir ses talents et de les cultiver.
              </p>
              <p>
                Avec cette plateforme <strong className="text-[#166534]">Mazid School Connect</strong>, nous franchissons une nouvelle étape : celle de la transparence, de la proximité et de l&apos;innovation. Parents, enseignants, élèves — nous sommes plus que jamais une communauté.
              </p>
              <p className="text-[#1E3A8A] font-heading text-xl font-bold pt-2">Faisons mieux. Ensemble.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-20 bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: "Mission", text: "Former des élèves compétents, autonomes et engagés, prêts à réussir dans un monde en pleine mutation." },
              { icon: Eye, title: "Vision", text: "Devenir une référence éducative en Afrique de l'Ouest, alliant excellence académique et innovation pédagogique." },
              { icon: Compass, title: "Valeurs", text: "Discipline, excellence, innovation, responsabilité — quatre piliers qui guident notre quotidien." },
            ].map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200" data-testid={`mvv-card-${i}`}>
                <div className="w-14 h-14 rounded-xl bg-[#166534]/10 flex items-center justify-center mb-5">
                  <b.icon className="h-7 w-7 text-[#166534]" strokeWidth={1.6} />
                </div>
                <h3 className="font-heading font-bold text-2xl text-[#1E3A8A] mb-3">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organisation */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="chip mb-4"><span>Organisation pédagogique</span></div>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1E3A8A] leading-tight tracking-tight mb-12 max-w-3xl">
            Une équipe engagée, des moyens à la hauteur de nos ambitions.
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <img src={ASSETS.building} alt="Bâtiment de l'école" className="rounded-2xl shadow-xl" />
            <div className="space-y-4">
              {[
                "Un fondateur visionnaire engagé pour l'éducation guinéenne",
                "Une équipe dirigeante expérimentée et passionnée",
                "Des enseignants qualifiés, formés en continu",
                "Des infrastructures modernes et adaptées",
                "Un suivi personnalisé pour chaque élève",
                "Une plateforme numérique de pointe pour les familles",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`org-point-${i}`}>
                  <CheckCircle2 className="h-6 w-6 text-[#166534] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 leading-relaxed">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
